import React from 'react';
import { Modal, Form, Select, DatePicker, Input, Button, InputNumber, Checkbox } from 'antd';
import { ContractorProjectDetailsDto, PaymentMethod } from 'api/models';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface CreateContractorPaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  projects: ContractorProjectDetailsDto[];
  onProjectSelect: (value: number) => void;
}

export const CreateContractorPaymentModal: React.FC<CreateContractorPaymentModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  projects,
  onProjectSelect,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethod>(PaymentMethod.Cash);
  const [isPaid, setIsPaid] = React.useState(false);

  const handlePaymentMethodChange = (value: PaymentMethod) => {
    setSelectedPaymentMethod(value);
    if (value === PaymentMethod.Cash) {
      form.setFieldValue('transactionReference', undefined);
    }
  };

  return (
    <Modal title={t('payments:create', 'Create Payment')} open={visible} onCancel={onCancel} footer={null}>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item name="projectId" label={t('payments:project', 'Project')} rules={[{ required: true }]}>
          <Select onChange={onProjectSelect}>
            {projects?.map(project => (
              <Select.Option key={project.projectId} value={project.projectId}>
                {project.projectName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="amount" label={t('payments:amount', 'Amount')} rules={[{ required: true }]}>
          <InputNumber
            className="w-full"
            style={{ width: '100%' }}
            formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={value => value!.replace(/€\s?|(,*)/g, '')}
          />
        </Form.Item>

        <Form.Item name="dueDate" label={t('payments:due-date', 'Due Date')} rules={[{ required: true }]}>
          <DatePicker className="w-full" />
        </Form.Item>

        <Form.Item name="paymentMethod" label={t('payments:method', 'Payment Method')} rules={[{ required: true }]}>
          <Select onChange={handlePaymentMethodChange}>
            <Select.Option value={PaymentMethod.Cash}>{t('payments:cash', 'Cash')}</Select.Option>
            <Select.Option value={PaymentMethod.Bank}>{t('payments:bank', 'Bank')}</Select.Option>
            <Select.Option value={PaymentMethod.Crypto}>{t('payments:crypto', 'Crypto')}</Select.Option>
            <Select.Option value={PaymentMethod.Other}>{t('payments:other', 'Other')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="isPaid" valuePropName="checked">
          <Checkbox
            onChange={e => {
              setIsPaid(e.target.checked);
              if (!e.target.checked) {
                form.setFieldValue('paidDate', undefined);
              }
            }}
          >
            {t('payments:already-paid', 'Already Paid')}
          </Checkbox>
        </Form.Item>

        {isPaid && (
          <>
            {(selectedPaymentMethod === PaymentMethod.Bank || selectedPaymentMethod === PaymentMethod.Crypto) && (
              <Form.Item
                name="transactionReference"
                label={t('payments:transaction-reference', 'Transaction Reference')}
                rules={[
                  {
                    required: true,
                    message: t(
                      'payments:transaction-reference-required',
                      'Transaction reference is required for paid non-cash payments',
                    ),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            )}
            <Form.Item
              name="paidDate"
              label={t('payments:paid-date', 'Paid Date')}
              rules={[{ required: isPaid, message: t('payments:paid-date-required', 'Please select paid date') }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
          </>
        )}

        <Form.Item name="notes" label={t('payments:notes', 'Notes')}>
          <Input.TextArea />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t('payments:create', 'Create')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
